import { useEffect, useState } from 'react';

import {
  ALL_OFFLINE_DATASETS,
  bootstrapOfflineDataset,
  type OfflineDatasetKind,
} from '@/lib/offline/datasetHydrator';
import { saveOfflineDataset } from '@/lib/offline/datasetCache';
import { sendOfflineDatasetTelemetry } from '@/lib/offline/telemetry';

const REQUIRED_DATASETS: OfflineDatasetKind[] = ALL_OFFLINE_DATASETS;

const nowMs = () => {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
};

export function useOfflineDatasetReady(tenantSlug?: string | null): boolean {
  const [ready, setReady] = useState(() => !tenantSlug);

  useEffect(() => {
    let cancelled = false;
    if (!tenantSlug) {
      setReady(true);
      return undefined;
    }
    setReady(false);

    const emitTelemetry = async (
      dataset: OfflineDatasetKind,
      source: 'cache' | 'network',
      durationMs: number,
      cachedAt: number | null,
      success: boolean,
      error?: string,
    ) => {
      try {
        await sendOfflineDatasetTelemetry({
          dataset,
          tenantSlug,
          source,
          durationMs,
          cachedAt,
          success,
          error: error ?? null,
        });
      } catch {
        // best-effort only
      }
    };

    const ensureDatasets = async () => {
      for (const dataset of REQUIRED_DATASETS) {
        const cacheStart = nowMs();
        let cachedRecord: Awaited<ReturnType<typeof bootstrapOfflineDataset>> | null = null;

        try {
          cachedRecord = await bootstrapOfflineDataset(tenantSlug, dataset);
        } catch (error) {
          await emitTelemetry(
            dataset,
            'cache',
            nowMs() - cacheStart,
            null,
            false,
            error instanceof Error ? error.message : String(error),
          );
        }

        const cachedAt = cachedRecord?.cachedAt ?? null;
        if (cachedRecord) {
          await emitTelemetry(dataset, 'cache', nowMs() - cacheStart, cachedAt, true);
        } else {
          await emitTelemetry(dataset, 'cache', nowMs() - cacheStart, null, false, 'MISS');
        }

        const networkStart = nowMs();
        try {
          const search = new URLSearchParams();
          if (cachedAt) {
            search.set('since', String(cachedAt));
          }
          const response = await fetch(
            `/api/offline/snapshots/${dataset}${search.size ? `?${search.toString()}` : ''}`,
            {
              method: 'GET',
              headers: {
                Accept: 'application/json',
                'X-Tenant': tenantSlug,
              },
              credentials: 'include',
            },
          );
          if (!response.ok) {
            await emitTelemetry(
              dataset,
              'network',
              nowMs() - networkStart,
              null,
              false,
              `HTTP_${response.status}`,
            );
            continue;
          }
          if (response.status === 204) {
            await emitTelemetry(dataset, 'network', nowMs() - networkStart, cachedAt, true);
            continue;
          }
          const payload = (await response.json()) as {
            version?: string;
            data: unknown;
            generatedAt?: number;
          };
          await saveOfflineDataset({
            tenantSlug,
            dataset,
            version: payload.version ?? `${dataset}-${Date.now()}`,
            data: payload.data,
            cachedAt: payload.generatedAt ?? Date.now(),
          });
          await emitTelemetry(
            dataset,
            'network',
            nowMs() - networkStart,
            payload.generatedAt ?? null,
            true,
          );
        } catch (error) {
          await emitTelemetry(
            dataset,
            'network',
            nowMs() - networkStart,
            null,
            false,
            error instanceof Error ? error.message : String(error),
          );
        }
      }

      if (!cancelled) {
        setReady(true);
      }
    };

    ensureDatasets().catch((error) => {
      console.error('[offline][dataset] failed to bootstrap datasets', error);
    });

    return () => {
      cancelled = true;
    };
  }, [tenantSlug]);

  return ready;
}
